import { useUpdateEffect } from "@react-hookz/web";
import { useAtom } from "jotai";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";

import { Stepper } from "~/components/stepper/Stepper";
import { toast } from "~/components/ui/toast";
import { UpdatingInfoStepConfirmation } from "~/components/updating-info/updating-info-step-confirmation";
import type { UpdatingInfoProps } from "~/components/updating-info/updating-info-step-introduction";
import { UpdatingInfoStepIntroduction } from "~/components/updating-info/updating-info-step-introduction";
import { UpdatingInfoThankYou } from "~/components/updating-info/updating-info-thank-you";
import { XChooseHeader } from "~/components/x-choose-header/x-choose-header";
import { useSetInfoAsUpToDateMutation } from "~/graphql/graphql.gen";
import type { BrandMatch } from "~/routes/root";
import { brandState } from "~/stores/brand";
import { getCorrectStep } from "~/utils/utils";

export function UpdatingInfo() {
  const [brand, setBrand] = useAtom(brandState);
  const { brandId = "", step: stepParam } = useParams<BrandMatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const steps = [
    {
      label: t("updating_info.introduction.name"),
      component: UpdatingInfoStepIntroduction,
    },
    {
      label: t("updating_info.confirmation.name"),
      component: UpdatingInfoStepConfirmation,
    },
  ];

  const [step, setStep] = useState<number>(
    getCorrectStep(stepParam, steps.length - 1),
  );
  const [isInfoUpToDate, setIsInfoUpToDate] = useState(false);

  const {
    mutateAsync: setInfoAsUpToDateMutation,
    isPending: loadingSetInfoAsUpToDate,
    error: errorSetInfoAsUpToDate,
  } = useSetInfoAsUpToDateMutation();

  useUpdateEffect(() => {
    setStep(getCorrectStep(stepParam, steps.length - 1));
  }, [stepParam]);

  useEffect(() => {
    if (errorSetInfoAsUpToDate) {
      toast.error(t("updating_info.confirmation_step.generic_error"));
    }
  }, [errorSetInfoAsUpToDate]);

  const goToNextStep = () => {
    if (step === 1) {
      onEndOnboarding();
    } else {
      navigate(`/${brandId}/updating-info/${step + 1}`);
      setStep(step + 1);
    }
  };

  const onEndOnboarding = async () => {
    await setInfoAsUpToDateMutation({});

    setIsInfoUpToDate(true);
    if (!brand) return;
    setBrand({
      ...brand,
      need_refresh_info: false,
    });
  };

  const Step = steps[step]?.component as React.ElementType<UpdatingInfoProps>;

  return (
    <>
      {isInfoUpToDate ? (
        <UpdatingInfoThankYou />
      ) : (
        <div className="onboarding mx-auto w-full px-4 py-10 sm:max-w-4.5xl sm:px-6">
          <XChooseHeader brandName={brand?.name ?? undefined}>
            <Stepper
              steps={steps}
              currentStep={step}
              className="w-full sm:max-w-[22.5rem]"
            />
          </XChooseHeader>
          <main className="sm:px-[4.75rem]">
            <Routes>
              <Route
                path="*"
                element={
                  <Step
                    goToNextStep={goToNextStep}
                    loading={loadingSetInfoAsUpToDate}
                  />
                }
              />
            </Routes>
          </main>
        </div>
      )}
    </>
  );
}
